import AsyncStorage from '@react-native-async-storage/async-storage'

import * as types from './types'
import { finishOnboarding } from './global'
import DirectusApi from '../../lib/directus.api'
import { getTokenExpiryDate } from '../../lib/utils'

type Credentials = {
  username: string
  password: string
}

const registerUserSuccess = () => ({
  type: types.REGISTER_USER,
})

export const logoutUserSuccess = (sessionExpired?: boolean) => ({
  type: types.LOGOUT_USER,
  payload: {
    sessionExpired
  }
})

const loadDataFromStorageSuccess = (auth: unknown) => {
  return {
    type: types.AUTH_LOAD_USER,
    auth,
  }
}

const loadDataFromStorageFailure = (error: string) => {
  return {
    type: types.AUTH_LOAD_ERROR,
    error,
  }
}

export const loadProfileData = () => async (dispatch: Function, state: Function) => {
  const { accessToken, id } = state().user

  if (!accessToken) return Promise.reject(new Error('User is not logged in'))

  try {
    const { data: { data: result } } = await DirectusApi.get(`/users/me`)
    const {
      email, email_verified, gdpr_accepted
    } = result

    const storeValues = [
      ['@HGStore:id', id],
      ['@HGStore:email', email],
    ]
    AsyncStorage.multiSet(storeValues)

    dispatch({
      type: types.SET_PROFILE_DATA,
      payload: {
        id,
        email,
        emailVerified: !!email_verified,
        privacyAccepted: !!gdpr_accepted,
      },
    })
  } catch (reason) {
    throw new Error(reason.message)
  }
}

export const loginUser = ({ username, password }: Credentials) => async (
  dispatch: Function,
  state: Function
) => {
  if (!username.length || !password.length) {
    return Promise.reject(new Error('auth/wrong-password'))
  }

  try {
    await DirectusApi.post('/custom/auth/login', {
      email: username,
      password,
    })
    const { data: { data: {
      access_token: accessToken, refresh_token: refreshToken, expires: expiresInMs
    } } } = await DirectusApi.post('/auth/login', {
      email: username,
      password,
      mode: 'json'
    })

    const { data: { data: { id, email } } } = await DirectusApi.get('/users/me', {
      params: {
        fields: ['id', 'email']
      },
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    const tokenExpiryDate = getTokenExpiryDate(expiresInMs)
    dispatch({
      type: types.LOGIN_USER,
      payload: {
        id,
        email,
        accessToken,
        refreshToken,
        tokenExpires: tokenExpiryDate
      }
    })
    const storeValues = [
      ['@HGStore:accessToken', accessToken],
      ['@HGStore:refreshToken', refreshToken],
      ['@HGStore:tokenExpires', tokenExpiryDate],
    ]
    AsyncStorage.multiSet(storeValues)

    await dispatch(loadProfileData())
    dispatch(finishOnboarding())
  } catch (reason) {
    console.log(JSON.stringify(reason))
    return Promise.reject(new Error('auth/wrong-password'))
  }
}

export const logoutUser = (sessionExpired?: boolean) => (dispatch: any, state: any) => {
  const { refreshToken } = state().user

  try {
    const allKeys = [
      '@HGStore:id',
      '@HGStore:email',
      '@HGStore:skipOnboarding',
      '@HGStore:patientGroup',
      '@HGStore:tokenExpires',
      '@HGStore:accessToken',
      '@HGStore:refreshToken',
    ]
    AsyncStorage.multiRemove(allKeys)
  } catch (e) {
    // handle error
  }
  DirectusApi.post('/auth/logout', {
    refresh_token: refreshToken
  })

  return dispatch(logoutUserSuccess(sessionExpired))
}

export const validateRegistrationCode = ({
  registrationCode,
}: {
  registrationCode: string
}) => async (dispatch: Function, state: Function) => {
  // 15/16-stellig, numerisch
  // const isValidCode = /^-?[\d.]{15,16}(?:e-?\d+)?$/.test(registrationCode)

  // const isValidCode = /^-?[\d.]+(?:e-?\d+)?$/.test(registrationCode)
  // if (!isValidCode) {
  //   return Promise.reject()
  // }
  if (!registrationCode) return Promise.reject(new Error('No registration code given'))

  try {
    const { data: { data: result } } = await DirectusApi.post(`/custom/efn/validate`, {
      efn: registrationCode
    })
    return result
  } catch (reason) {
    throw new Error(reason.message)
  }
}

export const refreshUser = () => async (dispatch: Function, state: Function): Promise<any> => {
  const { refreshToken, loggedIn } = state().user
  if (!loggedIn || !refreshToken) return Promise.reject()

  try {
    const { data: { data: result } } = await DirectusApi.post('/auth/refresh', {
      refresh_token: refreshToken
    })
    const { access_token: accessToken, refresh_token: newRefreshToken, expires: expiresInMs } = result
    const tokenExpiryDate = getTokenExpiryDate(expiresInMs)
    const storeValues = [
      ['@HGStore:accessToken', accessToken],
      ['@HGStore:tokenExpires', tokenExpiryDate],
      ['@HGStore:refreshToken', refreshToken],
    ]
    AsyncStorage.multiSet(storeValues)
    dispatch({
      type: types.REFRESH_USER,
      payload: {
        accessToken,
        refreshToken: newRefreshToken,
        tokenExpiryDate
      }
    })
    return result
  } catch (reason) {
    dispatch(logoutUser(true))
  }
}

export const registerUser = ({
  username,
  password,
  registrationCode,
}: {
  username: string
  password: string
  registrationCode: string
}) => async (dispatch: Function, state: Function) => {
  if (!username.length || !password.length || !registrationCode.length) return Promise.reject()

  const params = {
    email: username,
    password,
    efn: registrationCode,
  }

  try {
    await DirectusApi.post('/custom/user/register', params)
    dispatch(registerUserSuccess())
    dispatch(loginUser({ username, password }))
  } catch (reason) {
    const error = reason.response.errors[0]
    throw new Error(error.message)
  }
}

export const loadDataFromStorage = () => (
  dispatch: Function,
  state: Function
) => {
  const keys = [
    '@HGStore:id',
    '@HGStore:email',
    '@HGStore:skipOnboarding',
    '@HGStore:patientGroup',
    '@HGStore:tokenExpires',
    '@HGStore:accessToken',
    '@HGStore:refreshToken',
  ]

  return AsyncStorage.multiGet(keys)
    .then((data) => {
      const auth = {
        id: data[0][1] || null,
        email: data[1][1] || null,
        skipOnboarding: !!data[2][1],
        patientGroup: data[3][1] || null,
        tokenExpires: data[4][1] || null,
        accessToken: data[5][1] || null,
        refreshToken: data[6][1] || null,
      }
      dispatch(loadDataFromStorageSuccess(auth))
    })
    .catch((err) => {
      dispatch(loadDataFromStorageFailure(err))
      throw (new Error('Failed fetching storage values'))
    })
}

export const requestPasswordReset = ({ email }: { email: string }) => async (dispatch: Function, state: Function) => {
  try {
    const { data: { data: result } } = await DirectusApi.post('/custom/user/request-password-reset', {
      email
    })
    return result
  } catch (reason) {
    return new Error(reason.message)
  }
}

export const resetUserPassword = ({ newPassword, token }: { newPassword: string, token: string }) => async (dispatch: Function, state: Function) => {
  try {
    const { data: { data: result } } = await DirectusApi.post('/auth/password/reset', {
      token, password: newPassword
    })
    dispatch({ type: types.RESET_PASSWORD })
    return result
  } catch (reason) {
    throw new Error(reason.message)
  }
}

export const verifyEmailAddress = ({ token }: { email?: string, token: string }) => async (dispatch: Function, state: Function) => {

  try {
    const { data: { data: me } } = await DirectusApi.get(`/users/me`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const { data: { data: result } } = await DirectusApi.patch(`/users/${me.id}`, {
      email_verified: new Date().toISOString()
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return result
  } catch (reason) {
    throw new Error(reason.message)
  }
}

export const changeUserPassword = ({ newPassword }: { newPassword: string }) => async (dispatch: Function, state: Function) => {
  const { id } = state().user

  try {
    const { data: { data: result } } = await DirectusApi.patch(`/users/${id}`, {
      password: newPassword
    })
    dispatch({
      type: types.CHANGE_USER_PASSWORD,
    })
    return result
  } catch (reason) {
    throw new Error(reason.message)
  }
}

export const changeUserEmail = ({ newEmail }: { newEmail: string }) => async (
  dispatch: Function,
  state: Function
) => {
  const { id } = state().user

  try {
    const { data: { data: result } } = await DirectusApi.patch(`/users/${id}`, {
      email: newEmail
    })
    const { email } = result
    dispatch({
      type: types.CHANGE_USER_EMAIL,
      email,
    })

    if (email) {
      const storeValues = [['@HGStore:email', email]]
      AsyncStorage.multiSet(storeValues)
    }
    return result
  } catch (reason) {
    throw new Error(reason.message)
  }

}

export const requestVerifyEmail = () => async (dispatch: Function, state: Function) => {
  const { accessToken } = state().user

  try {
    const { data: { data: result } } = await DirectusApi.post('/custom/user/request-verify-email', null, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    })
    dispatch({
      type: types.REQUEST_VERIFY_EMAIL_ADDRESS
    })
    return result
  } catch (reason) {
    throw new Error(reason.message)
  }
}

export const setPrivacySettings = (privacyAccepted: boolean, newsletterSubscribed?: boolean) => async (dispatch: Function, state: Function) => {
  const { data: { data: result } } = await DirectusApi.patch('/custom/user/gdpr_newsletter', {
    gdpr: privacyAccepted,
    newsletter: newsletterSubscribed
  })

  dispatch({
    type: types.SET_PRIVACY_SETTINGS
  })
  return result
}
